// =============================================
// cloudflareService — zarzadzanie kontami CF + zone (domenami)
//
// Kazde konto CF ma wlasny token API (Zone:Edit) trzymany w DB
// `cloudflare_accounts`. Wywolania ida przez proxy /api/cloudflare/ (nginx
// przepisuje na api.cloudflare.com/client/v4/*) — token dosylamy naglowkiem
// Authorization: Bearer <token>, proxy tylko go przepuszcza (za brama auth).
//
// Pelen loop domeny: addZone(domena) -> zwraca name_servers (2 szt.) ->
// te NS wklejasz w panelu "Zmiana DNS" (AfterMarket ns/set) -> domena
// wskazuje na Cloudflare.
// =============================================

const CF_BASE = "/api/cloudflare";

export interface CfZone {
  id: string;
  name: string;
  status: string; // "active" | "pending" | ...
  name_servers?: string[];
  account?: { id: string; name?: string };
}

export interface CfAccount {
  id: string;
  name: string;
}

interface CfResponse<T> {
  success: boolean;
  result: T;
  errors?: Array<{ code: number; message: string }>;
  result_info?: { page: number; total_pages: number };
}

export class CloudflareError extends Error {}

async function cfFetch<T>(token: string, path: string, init: RequestInit = {}): Promise<CfResponse<T>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 60_000);
  try {
    const res = await fetch(`${CF_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(init.headers || {}),
      },
      signal: controller.signal,
    });

    let json: any = null;
    try {
      json = await res.json();
    } catch {
      throw new CloudflareError(
        `Cloudflare zwrocil odpowiedz nie-JSON (HTTP ${res.status}) — sprawdz czy proxy /api/cloudflare/ jest wdrozone.`,
      );
    }

    if (!json?.success) {
      const msg = json?.errors?.map((e: any) => `${e.code}: ${e.message}`).join("; ") || `HTTP ${res.status}`;
      throw new CloudflareError(msg);
    }
    return json as CfResponse<T>;
  } catch (err) {
    if (err instanceof CloudflareError) throw err;
    if ((err as Error)?.name === "AbortError") throw new CloudflareError("Cloudflare nie odpowiedzial w 60 s.");
    throw new CloudflareError(`Polaczenie z Cloudflare nieudane: ${(err as Error)?.message || err}`);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Sprawdza, czy token jest wazny.
 * Tokeny uzytkownika: GET /user/tokens/verify. Tokeny KONTA (prefiks `cfat_`,
 * tworzone w Manage Account -> API Tokens) na tym endpoincie dostaja
 * "1000: Invalid API Token" mimo ze dzialaja — wtedy fallback: GET /accounts.
 */
export async function verifyToken(token: string): Promise<{ ok: boolean; error?: string }> {
  try {
    await cfFetch<{ status: string }>(token, "/user/tokens/verify");
    return { ok: true };
  } catch (err) {
    const first = err instanceof Error ? err.message : String(err);
    try {
      const res = await cfFetch<CfAccount[]>(token, "/accounts?per_page=1");
      if (res.success) return { ok: true };
    } catch {
      // oba endpointy odrzucily
    }
    return { ok: false, error: first };
  }
}

/** Konta dostepne dla tokena — do ustawienia account_id przy dodawaniu zone. */
export async function listAccounts(token: string): Promise<CfAccount[]> {
  const res = await cfFetch<CfAccount[]>(token, "/accounts?per_page=50");
  return res.result || [];
}

/** Wszystkie zone (domeny) widoczne dla tokena — z name_servers i statusem. */
export async function listZones(token: string): Promise<CfZone[]> {
  const all: CfZone[] = [];
  let page = 1;
  for (let guard = 0; guard < 20; guard++) {
    const res = await cfFetch<CfZone[]>(token, `/zones?per_page=50&page=${page}`);
    all.push(...(res.result || []));
    const totalPages = res.result_info?.total_pages ?? 1;
    if (page >= totalPages) break;
    page++;
  }
  return all;
}

// ---------------------------------------------------------------------------
// Zony ze WSZYSTKICH kont panelu (zakladka "Domeny w CF" + kreator).
// ---------------------------------------------------------------------------

export interface CfZoneWithAccount extends CfZone {
  /** cloudflare_accounts.id (UUID z DB) — NIE zone.account.id z CF. */
  cfAccountId: string;
  cfAccountLabel: string;
  /** Konto ma account_id → moze tworzyc zony (addZone). */
  cfAccountHasAccountId: boolean;
}

export interface ListZonesAllResult {
  zones: CfZoneWithAccount[];
  /** Konta, dla ktorych listZones padlo (zly token, 429, siec) — reszta dziala. */
  failures: Array<{ cfAccountId: string; label: string; error: string }>;
  zoneCountByAccount: Record<string, number>;
}

/**
 * Zony ze wszystkich kont — sekwencyjnie po kontach (2–5 kont, bez 429),
 * blad jednego konta nie przerywa reszty. Ta sama zona widoczna z dwoch
 * tokenow → zostaje pierwsza (po kolejnosci kont).
 */
export async function listZonesAllAccounts(
  accounts: Array<{ id: string; label: string; api_token: string; account_id?: string | null }>,
): Promise<ListZonesAllResult> {
  const zones: CfZoneWithAccount[] = [];
  const failures: ListZonesAllResult["failures"] = [];
  const zoneCountByAccount: Record<string, number> = {};
  const seen = new Set<string>();

  for (const acc of accounts) {
    zoneCountByAccount[acc.id] = 0;
    try {
      const list = await listZones(acc.api_token);
      for (const z of list) {
        if (seen.has(z.id)) continue;
        seen.add(z.id);
        zones.push({
          ...z,
          cfAccountId: acc.id,
          cfAccountLabel: acc.label,
          cfAccountHasAccountId: !!acc.account_id?.trim(),
        });
        zoneCountByAccount[acc.id]++;
      }
    } catch (err) {
      failures.push({ cfAccountId: acc.id, label: acc.label, error: err instanceof Error ? err.message : String(err) });
    }
  }

  return { zones, failures, zoneCountByAccount };
}

/**
 * Dodaje domene do Cloudflare (tworzy zone). Zwraca m.in. name_servers —
 * te 2 nameservery ustawiasz potem u rejestratora (panel "Zmiana DNS").
 * account_id wymagany przez CF; jesli konto ma tylko jedno, mozna je pobrac
 * z listAccounts i podac tutaj.
 */
export async function addZone(token: string, domain: string, accountId: string): Promise<CfZone> {
  const res = await cfFetch<CfZone>(token, "/zones", {
    method: "POST",
    body: JSON.stringify({ name: domain.trim().toLowerCase(), account: { id: accountId }, type: "full" }),
  });
  return res.result;
}

// ---------------------------------------------------------------------------
// Rekordy DNS (wymaga na tokenie uprawnienia DNS:Edit — samo Zone:Edit nie
// wystarczy do zapisu rekordow; odczyt dziala z DNS:Read).
// ---------------------------------------------------------------------------

export type CfDnsRecordType = "A" | "AAAA" | "CNAME" | "TXT" | "MX" | "NS";
export const CF_DNS_RECORD_TYPES: CfDnsRecordType[] = ["A", "AAAA", "CNAME", "TXT", "MX", "NS"];

export interface CfDnsRecord {
  id: string;
  zone_id?: string;
  zone_name?: string;
  type: CfDnsRecordType | string;
  name: string; // FQDN, np. "www.przyklad.pl" albo "przyklad.pl" (root)
  content: string;
  proxied?: boolean;
  proxiable?: boolean;
  ttl: number; // 1 = auto
  priority?: number; // MX
  comment?: string | null;
  modified_on?: string;
}

export interface CfDnsRecordInput {
  type: CfDnsRecordType;
  name: string; // "@" / "www" / FQDN — normalizujemy do FQDN w createDnsRecord
  content: string;
  proxied?: boolean;
  ttl?: number; // domyslnie 1 (auto)
  priority?: number;
  comment?: string;
}

/** "@" -> zone, "www" -> www.zone, FQDN zostaje bez zmian. */
export function toFqdn(name: string, zoneName: string): string {
  const n = name.trim().toLowerCase().replace(/\.$/, "");
  const z = zoneName.trim().toLowerCase();
  if (!n || n === "@" || n === z) return z;
  if (n.endsWith(`.${z}`)) return n;
  return `${n}.${z}`;
}

const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
export function isValidIPv4(ip: string): boolean {
  return IPV4_RE.test(ip.trim());
}

function buildRecordBody(input: CfDnsRecordInput, zoneName: string) {
  const body: Record<string, unknown> = {
    type: input.type,
    name: toFqdn(input.name, zoneName),
    content: input.content.trim(),
    ttl: input.ttl ?? 1,
  };
  if (input.type === "A" || input.type === "AAAA" || input.type === "CNAME") body.proxied = !!input.proxied;
  if (input.type === "MX") body.priority = input.priority ?? 10;
  if (input.comment?.trim()) body.comment = input.comment.trim();
  return body;
}

/** Wszystkie rekordy DNS zone (paginacja po 100). */
export async function listDnsRecords(token: string, zoneId: string): Promise<CfDnsRecord[]> {
  const all: CfDnsRecord[] = [];
  let page = 1;
  for (let guard = 0; guard < 20; guard++) {
    const res = await cfFetch<CfDnsRecord[]>(token, `/zones/${zoneId}/dns_records?per_page=100&page=${page}`);
    all.push(...(res.result || []));
    const totalPages = res.result_info?.total_pages ?? 1;
    if (page >= totalPages) break;
    page++;
  }
  return all;
}

export async function createDnsRecord(
  token: string, zoneId: string, zoneName: string, input: CfDnsRecordInput,
): Promise<CfDnsRecord> {
  const res = await cfFetch<CfDnsRecord>(token, `/zones/${zoneId}/dns_records`, {
    method: "POST",
    body: JSON.stringify(buildRecordBody(input, zoneName)),
  });
  return res.result;
}

export async function updateDnsRecord(
  token: string, zoneId: string, zoneName: string, recordId: string, input: CfDnsRecordInput,
): Promise<CfDnsRecord> {
  const res = await cfFetch<CfDnsRecord>(token, `/zones/${zoneId}/dns_records/${recordId}`, {
    method: "PATCH",
    body: JSON.stringify(buildRecordBody(input, zoneName)),
  });
  return res.result;
}

export async function deleteDnsRecord(token: string, zoneId: string, recordId: string): Promise<void> {
  await cfFetch<{ id: string }>(token, `/zones/${zoneId}/dns_records/${recordId}`, { method: "DELETE" });
}

export interface PointDomainResult {
  root: { action: "created" | "updated" | "unchanged"; record: CfDnsRecord };
  www: { action: "created" | "updated" | "unchanged"; record: CfDnsRecord };
}

/**
 * Szybka sciezka "podepnij IP": upsert rekordu A dla root (@) i www.
 * Istniejacy A o tej nazwie -> PATCH (content/proxied), brak -> POST.
 * Nie rusza rekordow innych typow (CNAME na www trzeba usunac recznie —
 * CF nie pozwala miec A i CNAME pod ta sama nazwa).
 */
export async function pointDomainToIp(
  token: string, zoneId: string, zoneName: string, ip: string, proxied: boolean,
): Promise<PointDomainResult> {
  const cleanIp = ip.trim();
  if (!isValidIPv4(cleanIp)) throw new CloudflareError(`Niepoprawny adres IPv4: "${cleanIp}"`);

  const existing = await listDnsRecords(token, zoneId);
  const upsert = async (name: "@" | "www") => {
    const fqdn = toFqdn(name, zoneName);
    const input: CfDnsRecordInput = { type: "A", name: fqdn, content: cleanIp, proxied, ttl: 1 };
    const cur = existing.find((r) => r.type === "A" && r.name.toLowerCase() === fqdn);
    if (cur) {
      if (cur.content === cleanIp && !!cur.proxied === proxied) return { action: "unchanged" as const, record: cur };
      return { action: "updated" as const, record: await updateDnsRecord(token, zoneId, zoneName, cur.id, input) };
    }
    return { action: "created" as const, record: await createDnsRecord(token, zoneId, zoneName, input) };
  };

  const root = await upsert("@");
  const www = await upsert("www");
  return { root, www };
}
